import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
} from '@angular/core';
import { UiModalComponent } from '@components';
import { ModalResultType } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { ICSVParseResult } from '@services/csv-parser.service';

import {
    BehaviorSubject,
    Subject,
} from 'rxjs';

import { BulkAddItemsModalService } from './bulk-add-items.service';
import {
    BulkCommitType,
    CSVFields,
    IBulkAddQueueItemsFailedResponse,
} from './bulk-add-items.types';

@Component({
    templateUrl: './bulk-add-items.modal.html',
    styleUrls: [
        './../../../../../styles/modal/file-upload.modal.scss',
        './bulk-add-items.modal.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkAddItemsModalComponent
    extends UiModalComponent<any>
    implements OnDestroy {

    public BulkCommitType = BulkCommitType;
    public commitTypeOption = BulkCommitType.ProcessAllIndependently;

    public get fileName(): string {
        return this.file ? this.file.name : '';
    }

    public get disableSubmit(): boolean {
        return !this.file ||
            !this.parsed ||
            this.parsed.errors.length > 0 ||
            this.parsed.data.length === 0 ||
            this.responseFailedItems.length > 0;
    }

    public get helpInfo() {
        return {
            supportedDateFormats: this._bulkAddService.supportedDateFormats,
            priorityValues: ['"Low" / 1', '"Normal" / 2', '"High" / 3'],
        };
    }

    public file?: File;
    public parsed?: ICSVParseResult<CSVFields>;
    public loading$ = new BehaviorSubject(false);
    public responseFailedItems: IBulkAddQueueItemsFailedResponse[] = [];

    private _destroyed$ = new Subject<void>();
    private _bulkAddService = this._injector.get<BulkAddItemsModalService>(BulkAddItemsModalService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);

    public getScrollHeight(itemsCount: number): number {
        return Math.min(6, itemsCount) * 41;
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public onClear() {
        this.file = undefined;
        this.parsed = undefined;
        this.responseFailedItems = [];
    }

    public onFileChange([file]: File[]) {
        this.onClear();
        this.loading$.next(true);
        this.file = file;
        this._bulkAddService.parseFile(file)
            .subscribe(
                (parsedResponse) => {
                    this.parsed = parsedResponse;
                    this.loading$.next(false);
                },
                (err) => {
                    this._snack.error(err);
                    this.onClear();
                    this.loading$.next(false);
                },
            );
    }

    public onSubmit() {
        this.loading$.next(true);
        this._bulkAddService.bulkAddQueueItems(
            this.parsed!.data,
            this.context.data.name,
            this.commitTypeOption,
        ).subscribe(
            (response) => {
                if (!response.success) {
                    this.responseFailedItems = response.failedItems || [];
                    this._snack.error(response.message);
                    this.loading$.next(false);
                    return;
                }

                this._snack.success(
                    this._translateService.instant('queue_bulk_add_items_success', {
                        count: this.parsed!.data.length,
                    }),
                );

                this._dialogRef.close({ type: ModalResultType.Add });
            },
            () => {
                this.loading$.next(false);
            },
        );
    }

    public onDownloadUnprocessedRows() {
        if (!this.responseFailedItems.length) { return; }

        const unprocessedRows = this.responseFailedItems
            .map(failed => this.parsed!.data[failed.row - 1]);

        this._bulkAddService.downloadFile(
            this.file!.name,
            this._translateService.instant('queue_bulk_output_csv_filename_suffix'),
            unprocessedRows,
        );
    }
}
